import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock, User } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, Lines } from "../components/site/Motion";
import { IMG } from "../lib/images";
import { categoryMeta } from "../lib/guideCategories";

const slugify = (s) =>
  s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function renderInline(text) {
  return text.split(/(\[[^\]]+\]\([^)]+\))/g).map((part, j) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!m) return part;
    const [, label, url] = m;
    if (url.startsWith("http")) {
      return <a key={j} href={url} target="_blank" rel="noopener noreferrer" className="text-swapa-yellow underline underline-offset-4 transition-colors hover:text-white">{label}</a>;
    }
    return <Link key={j} to={url} className="text-swapa-yellow underline underline-offset-4 transition-colors hover:text-white">{label}</Link>;
  });
}

function Body({ body }) {
  return body.split("\n\n").map((block, i) => {
    if (block.startsWith("### ")) {
      return <h3 key={i} className="mt-10 font-display text-xl font-bold uppercase tracking-tight text-zinc-100">{block.slice(4)}</h3>;
    }
    if (block.startsWith("## ")) {
      const text = block.slice(3);
      return <h2 key={i} id={slugify(text)} className="mt-14 scroll-mt-28 font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">{text}</h2>;
    }
    if (block.startsWith("- ")) {
      return (
        <ul key={i} className="mt-6 space-y-2.5">
          {block.split("\n").map((li, k) => (
            <li key={k} className="flex gap-3 text-base leading-[1.8] text-zinc-400">
              <span className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-swapa-yellow" />
              <span>{renderInline(li.replace(/^- /, ""))}</span>
            </li>
          ))}
        </ul>
      );
    }
    return <p key={i} className="mt-6 text-base leading-[1.9] text-zinc-400">{renderInline(block)}</p>;
  });
}

function tocFrom(body) {
  return body.split("\n\n")
    .filter((b) => b.startsWith("## "))
    .map((b) => ({ id: slugify(b.slice(3)), label: b.slice(3) }));
}

function JsonLd({ data }) {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />;
}

const CTA_BY_LEVEL = {
  soft: [{ to: "/tecnologia", label: "Scopri come è fatta SWAPA ZIP", testid: "guide-cta-soft" }],
  medium: [
    { to: "/tecnologia", label: "Scopri SWAPA ZIP", testid: "guide-cta-discover" },
    { to: "/test-drive", label: "Prenota un test drive", testid: "guide-cta-testdrive" },
  ],
  strong: [
    { to: "/test-drive", label: "Prenota un test drive", testid: "guide-cta-testdrive" },
    { to: "/preordina", label: "Preordina SWAPA ZIP con €200", testid: "guide-cta-preorder" },
  ],
};

export default function GuidePage() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [all, setAll] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setGuide(null);
    setNotFound(false);
    window.scrollTo(0, 0);
    api.get(`/guides/${slug}`)
      .then((r) => {
        setGuide(r.data);
        document.title = r.data.meta_title || `${r.data.title} | SWAPA`;
        const meta = document.querySelector('meta[name="description"]');
        if (meta && r.data.meta_description) meta.setAttribute("content", r.data.meta_description);
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) canonical.setAttribute("href", `https://swapa.it/guide/${r.data.slug}`);
        const og = { "og:title": r.data.title, "og:description": r.data.meta_description || r.data.excerpt, "og:type": "article", "og:image": `https://swapa.it${r.data.hero_image || "/assets/hero-poster.jpg"}` };
        Object.entries(og).forEach(([p, v]) => {
          let el = document.querySelector(`meta[property="${p}"]`);
          if (!el) { el = document.createElement("meta"); el.setAttribute("property", p); document.head.appendChild(el); }
          el.setAttribute("content", v);
        });
      })
      .catch(() => setNotFound(true));
    api.get("/guides").then((r) => setAll(r.data)).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="guide-notfound" className="flex min-h-[70vh] flex-col items-center justify-center bg-ink px-6 pt-24 text-center">
        <h1 className="font-display text-4xl font-extrabold uppercase text-white">Guida non trovata</h1>
        <Link to="/guide" className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-swapa-yellow">← Torna a SWAPA Guide</Link>
      </div>
    );
  }
  if (!guide) {
    return <div className="min-h-[70vh] bg-ink pt-24" data-testid="guide-loading" />;
  }

  const cat = categoryMeta(guide.category);
  const toc = tocFrom(guide.body || "");
  const published = (guide.created_at || "").slice(0, 10);
  const updated = (guide.updated_at || "").slice(0, 10);
  const faq = guide.faq || [];
  const sources = guide.sources || [];
  const related = (guide.related || [])
    .map((s) => all.find((g) => g.slug === s))
    .filter(Boolean)
    .slice(0, 3);
  const ctas = CTA_BY_LEVEL[guide.cta_level] || CTA_BY_LEVEL.medium;
  const fmtDate = (d) => new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.title,
    description: guide.meta_description || guide.excerpt,
    image: `https://swapa.it${guide.hero_image || "/assets/hero-poster.jpg"}`,
    datePublished: published,
    dateModified: updated || published,
    author: { "@type": "Organization", name: guide.author || "Redazione SWAPA" },
    publisher: { "@type": "Organization", name: "SWAPA", url: "https://swapa.it" },
    mainEntityOfPage: `https://swapa.it/guide/${guide.slug}`,
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "SWAPA", item: "https://swapa.it/" },
      { "@type": "ListItem", position: 2, name: "SWAPA Guide", item: "https://swapa.it/guide" },
      { "@type": "ListItem", position: 3, name: guide.title, item: `https://swapa.it/guide/${guide.slug}` },
    ],
  };
  const faqLd = faq.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  } : null;

  return (
    <article data-testid="guide-page" className="bg-ink pb-28 pt-28 sm:pt-36">
      <JsonLd data={articleLd} />
      <JsonLd data={breadcrumbLd} />
      {faqLd && <JsonLd data={faqLd} />}
      <div className="mx-auto max-w-[900px] px-5 sm:px-10">
        <nav aria-label="Breadcrumb" data-testid="guide-breadcrumb" className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          <Link to="/guide" data-testid="guide-back" className="inline-flex items-center gap-2 transition-colors hover:text-swapa-yellow">
            <ArrowLeft className="h-3.5 w-3.5" /> SWAPA Guide
          </Link>
          <span aria-hidden="true">/</span>
          <span className="text-zinc-400">{cat.label}</span>
        </nav>

        <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.3em] text-swapa-yellow" data-testid="guide-category">{cat.label}</p>
        <h1 className="mt-4 font-display font-extrabold uppercase leading-[0.95] tracking-tight">
          <Lines animate="load" lines={[guide.title]} className="text-3xl text-white sm:text-5xl" />
        </h1>

        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500" data-testid="guide-meta">
          <span className="inline-flex items-center gap-2"><User className="h-3 w-3" />{guide.author || "Redazione SWAPA"}</span>
          <span className="inline-flex items-center gap-2"><Clock className="h-3 w-3" />{guide.reading_time} min di lettura</span>
          {published && <span>Pubblicata il {fmtDate(published)}</span>}
          {updated && <span className="text-zinc-400">Ultimo aggiornamento: {fmtDate(updated)}</span>}
        </div>

        {guide.direct_answer && (
          <Reveal delay={0.2}>
            <div data-testid="guide-direct-answer" className="mt-10 border-l-2 border-swapa-yellow bg-white/[0.03] p-6 sm:p-8">
              <p className="text-base leading-[1.8] text-zinc-200 sm:text-lg">{renderInline(guide.direct_answer)}</p>
            </div>
          </Reveal>
        )}

        <Reveal delay={0.15}>
          <img src={guide.hero_image || IMG.guideCity} alt={guide.title} className="mt-10 aspect-[21/9] w-full border border-white/10 object-cover" loading="eager" />
        </Reveal>

        {toc.length > 2 && (
          <nav data-testid="guide-toc" className="mt-12 border border-white/10 p-6 sm:p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">In questa guida</p>
            <ol className="mt-4 space-y-2.5">
              {toc.map((t, i) => (
                <li key={t.id}>
                  <a href={`#${t.id}`} className="group inline-flex items-baseline gap-3 text-sm text-zinc-400 transition-colors hover:text-swapa-yellow">
                    <span className="font-mono text-[10px] text-zinc-600">{String(i + 1).padStart(2, "0")}</span>
                    {t.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div data-testid="guide-body" className="mt-4">
          <Body body={guide.body} />
        </div>

        {sources.length > 0 && (
          <section data-testid="guide-sources" className="mt-16 border-t border-white/10 pt-10">
            <h2 className="font-display text-lg font-bold uppercase tracking-tight text-white">Fonti</h2>
            <ul className="mt-5 space-y-2.5">
              {sources.map((s, i) => (
                <li key={i} className="text-sm text-zinc-500">
                  {s.url ? (
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-zinc-400 underline underline-offset-4 transition-colors hover:text-swapa-yellow">{s.label}</a>
                  ) : (
                    s.label
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {faq.length > 0 && (
          <section data-testid="guide-faq" className="mt-16 border-t border-white/10 pt-10">
            <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white">Domande frequenti</h2>
            <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
              {faq.map((f, i) => (
                <details key={i} className="group py-5" data-testid={`guide-faq-${i}`}>
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-medium text-zinc-100 transition-colors hover:text-swapa-yellow [&::-webkit-details-marker]:hidden">
                    {f.q}
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-zinc-500 transition-transform group-open:rotate-45" />
                  </summary>
                  <p className="mt-4 text-sm leading-[1.85] text-zinc-400">{renderInline(f.a)}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        <section data-testid="guide-swapa-box" className="mt-16 border border-swapa-yellow/30 bg-gradient-to-br from-swapa-yellow/[0.06] to-transparent p-8 sm:p-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-swapa-yellow">SWAPA ZIP</p>
          <p className="mt-4 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight text-white sm:text-3xl">
            La microcar elettrica<br />100% made per la città.
          </p>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-zinc-400">
            SWAPA ZIP è un quadriciclo leggero elettrico della categoria L6e: due posti, abitacolo chiuso,
            ricarica da presa domestica e design essenziale. Si guida da 14 anni con patente AM.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            {ctas.map((c) => (
              <Link key={c.testid} to={c.to} data-testid={c.testid}
                className="group inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:border-swapa-yellow hover:text-swapa-yellow">
                {c.label}
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>
        </section>

        {related.length > 0 && (
          <section data-testid="guide-related" className="mt-16 border-t border-white/10 pt-10">
            <h2 className="font-display text-lg font-bold uppercase tracking-tight text-white">Guide correlate</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-3">
              {related.map((g) => (
                <Link key={g.slug} to={`/guide/${g.slug}`} data-testid={`guide-related-${g.slug}`}
                  className="group border border-white/10 p-5 transition-colors duration-300 hover:border-swapa-yellow/50">
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-swapa-yellow">{categoryMeta(g.category).label}</p>
                  <p className="mt-3 font-display text-base font-bold uppercase leading-tight text-white group-hover:text-swapa-yellow">{g.title}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}
